import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';

import CustomCampaignAudienceStep from './steps/CustomCampaignAudienceStep';
import CustomCampaignChooseNextStep from './steps/CustomCampaignChooseNextStep';
import CustomCampaignChooseNextStepConditionalSplit from './steps/CustomCampaignChooseNextStepConditionalSplit';
import CustomCampaignChooseNextStepRandomSplit from './steps/CustomCampaignChooseNextStepRandomSplit';
import CustomCampaignChooseSendTypeStep from './steps/CustomCampaignChooseSendTypeStep';
import CustomCampaignChooseSplitAction from './steps/CustomCampaignChooseSplitAction';
import CustomCampaignContentStep from './steps/CustomCampaignContentStep';
import CustomCampaignNameStep from './steps/CustomCampaignNameStep';
import CustomCampaignPublishStep from './steps/CustomCampaignPublishStep';
import CustomCampaignRandomSplitStep from './steps/CustomCampaignRandomSplitStep';
import CustomCampaignScheduleStep from './steps/CustomCampaignScheduleStep';
import CustomCampaignSetupABTestStep from './steps/CustomCampaignSetupABTestStep';
import CustomCampaignSplitCriteriaStep from './steps/CustomCampaignSplitCriteriaStep';
import CustomCampaignTimeDelayStep from './steps/CustomCampaignTimeDelayStep';

type Props = {
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    customContinueHandler?: (() => void) | null; // New: custom continue handler
    dispatchCampaignAction: React.Dispatch<any>;
    initialContentSubStep?: 'selecting' | 'scheduling'; // New: for controlling initial content sub-step
    navigateToABTestScheduling?: (emailIndex: number) => void; // New: for A/B test scheduling navigation
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
    setContinueTooltip: (tooltip: string) => void;
    setContentPreviewId: (id: number) => void;
    setCustomContinueHandler?: (handler: (() => void) | null) => void; // New: setter for custom continue handler
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const CustomCampaignForm = ({
    activeSendIndex,
    activeStep,
    campaignState,
    contentPreviewId,
    customContinueHandler,
    dispatchCampaignAction,
    initialContentSubStep,
    navigateToABTestScheduling,
    setActiveSendIndex,
    setActiveStep,
    setContinueTooltip,
    setContentPreviewId,
    setCustomContinueHandler,
    setIsContinueEnabled,
    setNextStep,
    onContentUnavailableChange,
}: Props) => {
    switch (activeStep) {
        case CustomCampaignSteps.NAME_CAMPAIGN:
            return (
                <CustomCampaignNameStep
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.SELECT_AUDIENCE:
            return (
                <CustomCampaignAudienceStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.SELECT_CONTENT:
            return (
                <CustomCampaignContentStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    contentPreviewId={contentPreviewId}
                    dispatchCampaignAction={dispatchCampaignAction}
                    initialContentSubStep={initialContentSubStep}
                    setActiveSendIndex={setActiveSendIndex}
                    setContinueTooltip={setContinueTooltip}
                    setContentPreviewId={setContentPreviewId}
                    setCustomContinueHandler={setCustomContinueHandler}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                    onContentUnavailableChange={onContentUnavailableChange}
                />
            );
        case CustomCampaignSteps.CHOOSE_NEXT_STEP:
            return (
                <CustomCampaignChooseNextStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT:
            return (
                <CustomCampaignChooseNextStepConditionalSplit
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.CHOOSE_NEXT_STEP_RANDOM_SPLIT:
            return (
                <CustomCampaignChooseNextStepRandomSplit
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.SET_TIME_DELAY:
            return (
                <CustomCampaignTimeDelayStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.CHOOSE_SEND_TYPE:
            return (
                <CustomCampaignChooseSendTypeStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.CONDITIONAL_SPLIT:
        case 'set-split-criteria' as any:
            return (
                <CustomCampaignSplitCriteriaStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.RANDOM_SPLIT:
            return (
                <CustomCampaignRandomSplitStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.SELECT_PATH_ACTION:
            return (
                <CustomCampaignChooseSplitAction
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.SCHEDULE_CURATION:
            return (
                <CustomCampaignScheduleStep
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.SETUP_A_B_TEST:
            return (
                <CustomCampaignSetupABTestStep
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveStep={setActiveStep}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.PUBLISH_CAMPAIGN:
            return (
                <CustomCampaignPublishStep
                    campaignState={campaignState}
                    navigateToABTestScheduling={navigateToABTestScheduling}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        default:
            return <div>This step is invalid!</div>;
    }
};

export default CustomCampaignForm;
